import { defineMessages, type IntlShape } from 'react-intl';
import { type IssuedTicketRevokeReasonDTO as IssuedTicketRevokeReason } from '@/api/trafikportalen';
import { formatDateTime } from '@/helpers/formatting';

const issuedTicketRevokeReasonMessages = defineMessages({
  revoked_by_user: {
    defaultMessage: 'Avslutad av användare',
  },
  ticket_claim_deleted: {
    defaultMessage: 'Perioden togs bort',
  },
  department_deleted: {
    defaultMessage: 'Avdelningen togs bort',
  },
  collection_member_inactivated: {
    defaultMessage: 'Resenären inaktiverades',
  },
  claim_member_removed: {
    defaultMessage: 'Resenären togs bort från perioden',
  },
  claim_expired: {
    defaultMessage: 'Perioden löpte ut',
  },
  address_changed: {
    defaultMessage: 'Adressen ändrades',
  },
});

export const issuedTicketRevokeReasonLabel = (
  formatMessage: IntlShape['formatMessage'],
  reason: string,
) => {
  const descriptor =
    issuedTicketRevokeReasonMessages[reason as IssuedTicketRevokeReason];
  return descriptor ? formatMessage(descriptor) : reason;
};

const withRevokedAt = (
  formatMessage: IntlShape['formatMessage'],
  text: string | undefined,
  revokedAt: string | undefined,
) => {
  if (text && revokedAt) {
    return formatMessage(
      { defaultMessage: '{text}, {revokedAt}' },
      { text, revokedAt },
    );
  }

  return text ?? revokedAt;
};

export const issuedTicketRevokeReasonTitle = (
  formatMessage: IntlShape['formatMessage'],
  ticket: {
    state?: string;
    revoke_reason?: string;
    revoke_reason_detail?: string;
    updated_at?: string;
  },
  userRevokeLabel?: string,
) => {
  const isUserRevoke = ticket.revoke_reason === 'revoked_by_user';
  const reason =
    isUserRevoke && userRevokeLabel
      ? userRevokeLabel
      : ticket.revoke_reason
        ? issuedTicketRevokeReasonLabel(formatMessage, ticket.revoke_reason)
        : undefined;
  const detail = isUserRevoke ? ticket.revoke_reason_detail?.trim() : undefined;
  const explanation =
    reason && detail
      ? formatMessage(
          { defaultMessage: '{reason}, med anledning: "{detail}"' },
          { reason, detail },
        )
      : (reason ?? (detail || undefined));
  const revoked =
    ticket.state === 'revoked' ||
    ticket.state === 'marked_for_deletion' ||
    Boolean(explanation);
  const revokedAt =
    revoked && ticket.updated_at
      ? formatDateTime(ticket.updated_at)
      : undefined;

  return withRevokedAt(formatMessage, explanation, revokedAt);
};
